<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useDocArticle } from '@/composables/useDocArticle'
import { useDocCategory } from '@/composables/useDocCategory'
import { useDocCategories } from '@/composables/useDocCategories'
import { useScrollSpy } from '@/composables/useScrollSpy'
import { useDocSearch } from '@/composables/useDocSearch'
import { useDocsContext } from '@/composables/useDocsContext'
import { useLocale } from '@/composables/useLocale'
import { DocCategoryValues, DocArticleStatus } from '@/types/docs'
import type { DocCategory } from '@/types/docs'
import { renderMarkdown } from '@/utils/renderMarkdown'
import OrganizerLayout from '@/components/layout/OrganizerLayout.vue'
import DocsPublicLayout from '@/components/layout/DocsPublicLayout.vue'
import DocsTree from '@/components/common/docs/DocsTree.vue'
import DocsScrollspy from '@/components/common/docs/DocsScrollspy.vue'
import DocsArticleFeedback from '@/components/common/docs/DocsArticleFeedback.vue'
import DocsSearchPalette from '@/components/common/docs/DocsSearchPalette.vue'

const { t } = useI18n()
const route = useRoute()
const { base, isPublic } = useDocsContext()
const { currentLocale } = useLocale()
const { ensureLoaded } = useDocSearch()

const categoryParam = computed(() => route.params.category as string)
const slugParam = computed(() => route.params.article as string)

const isValidCategory = computed<boolean>(() =>
  (DocCategoryValues as string[]).includes(categoryParam.value),
)

const {
  article,
  loading: articleLoading,
  error: articleError,
  notFound,
  localeFallback,
  fetch: fetchArticle,
} = useDocArticle()

const {
  articles: categoryArticles,
  fetch: fetchCategory,
} = useDocCategory()

const {
  categories,
  fetch: fetchCategories,
} = useDocCategories()

const paletteOpen = ref(false)
const treeDrawerOpen = ref(false)

// Rendered, sanitised HTML from the Markdown body
const renderedHtml = computed<string>(() => {
  if (!article.value?.body) return ''
  return renderMarkdown(article.value.body)
})

// Scrollspy — headings come from the article resource
const headings = computed(() => article.value?.headings ?? [])
const { activeId } = useScrollSpy(headings.value)

// Watch headings so the spy reinitialises when the article changes
watch(headings, (newHeadings) => {
  if (newHeadings.length > 0) {
    // Scrollspy already set up reactively via onMounted inside the composable
    // Trigger a soft re-init by the component re-rendering
  }
})

function formatDate(dateStr: string | null): string {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  return d.toLocaleDateString(currentLocale.value, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

async function load(): Promise<void> {
  if (!isValidCategory.value) return
  await Promise.all([
    fetchArticle(categoryParam.value, slugParam.value),
    fetchCategory(categoryParam.value),
    fetchCategories(),
  ])
}

function handleGlobalKeydown(event: KeyboardEvent): void {
  if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
    event.preventDefault()
    paletteOpen.value = true
  }
}

onMounted(() => {
  void load()
  void ensureLoaded(currentLocale.value)
  document.addEventListener('keydown', handleGlobalKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleGlobalKeydown)
})

// Re-fetch when route params change
watch([categoryParam, slugParam], () => {
  void load()
})
</script>

<template>
  <component :is="isPublic ? DocsPublicLayout : OrganizerLayout">
    <!-- Not found state -->
    <div
      v-if="(!isValidCategory || notFound) && !articleLoading"
      class="max-w-2xl mx-auto px-4 py-20 text-center"
    >
      <p class="text-slate-300 text-lg font-medium mb-2">
        {{ t('docs.article.not_found_title') }}
      </p>
      <p class="text-slate-400 text-sm mb-6">
        {{ t('docs.article.not_found_body') }}
      </p>
      <RouterLink
        :to="base"
        class="text-sm text-emerald-400 hover:underline"
      >
        {{ t('docs.breadcrumb.docs') }}
      </RouterLink>
    </div>

    <!-- Main three-pane layout -->
    <div
      v-else
      class="flex min-h-screen"
    >
      <!-- Left pane: DocsTree — hidden on mobile, shown via drawer -->
      <aside
        class="hidden lg:block w-64 shrink-0 border-r border-slate-800 px-3 sticky top-16 self-start max-h-[calc(100vh-4rem)] overflow-y-auto"
      >
        <DocsTree
          :categories="categories"
          :current-category="categoryParam as DocCategory"
          :current-slug="slugParam"
          :articles-in-current-category="categoryArticles"
          :base="base"
        />
      </aside>

      <!-- Mobile tree drawer overlay -->
      <Teleport to="body">
        <div
          v-if="treeDrawerOpen"
          class="fixed inset-0 z-40 flex lg:hidden"
        >
          <!-- Backdrop -->
          <div
            class="absolute inset-0 bg-black/60"
            @click="treeDrawerOpen = false"
          />
          <!-- Drawer -->
          <div class="relative w-72 max-w-full bg-slate-900 border-r border-slate-800 h-full overflow-y-auto px-3">
            <div class="flex items-center justify-between py-4 px-3">
              <p class="text-sm font-semibold text-slate-300">
                {{ t('docs.article.open_tree') }}
              </p>
              <button
                type="button"
                class="text-slate-500 hover:text-white transition-colors"
                :aria-label="t('common.close')"
                @click="treeDrawerOpen = false"
              >
                <svg
                  class="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <DocsTree
              :categories="categories"
              :current-category="categoryParam as DocCategory"
              :current-slug="slugParam"
              :articles-in-current-category="categoryArticles"
              :base="base"
              @navigate="treeDrawerOpen = false"
            />
          </div>
        </div>
      </Teleport>

      <!-- Centre pane: article content -->
      <main class="flex-1 min-w-0 px-4 sm:px-8 py-8 max-w-3xl mx-auto lg:mx-0">
        <!-- Mobile: tree toggle + search button -->
        <div class="flex items-center gap-2 mb-4 lg:hidden">
          <button
            type="button"
            class="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white border border-slate-700 rounded-lg px-3 py-1.5 transition-colors"
            @click="treeDrawerOpen = true"
          >
            <svg
              class="w-3.5 h-3.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
            {{ t('docs.article.open_tree') }}
          </button>
          <button
            type="button"
            class="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white border border-slate-700 rounded-lg px-3 py-1.5 transition-colors"
            @click="paletteOpen = true"
          >
            <svg
              class="w-3.5 h-3.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            {{ t('docs.search.placeholder') }}
          </button>
        </div>

        <!-- Breadcrumb -->
        <nav
          aria-label="breadcrumb"
          class="flex items-center gap-2 text-xs text-slate-500 mb-4 flex-wrap"
        >
          <RouterLink
            :to="base"
            class="hover:text-slate-300 transition-colors"
          >
            {{ t('docs.breadcrumb.docs') }}
          </RouterLink>
          <span aria-hidden="true">/</span>
          <RouterLink
            :to="`${base}/${categoryParam}`"
            class="hover:text-slate-300 transition-colors"
          >
            {{ t(`docs.category.${categoryParam}.title`) }}
          </RouterLink>
          <template v-if="article">
            <span aria-hidden="true">/</span>
            <span class="text-slate-300 truncate max-w-xs">{{ article.title }}</span>
          </template>
        </nav>

        <!-- Loading skeleton -->
        <div
          v-if="articleLoading"
          class="space-y-4 animate-pulse"
          aria-busy="true"
        >
          <div class="h-7 bg-slate-700 rounded w-2/3" />
          <div class="h-3 bg-slate-700 rounded w-1/4" />
          <div class="h-3 bg-slate-700 rounded w-full mt-4" />
          <div class="h-3 bg-slate-700 rounded w-5/6" />
          <div class="h-3 bg-slate-700 rounded w-4/5" />
        </div>

        <!-- Error -->
        <div
          v-else-if="articleError"
          class="rounded-xl bg-red-500/10 border border-red-500/30 px-5 py-3 text-sm text-red-400"
        >
          {{ t('docs.article.error') }}
        </div>

        <!-- Article content -->
        <article v-else-if="article">
          <!-- Outdated banner -->
          <div
            v-if="article.status === DocArticleStatus.Outdated"
            class="mb-4 flex items-start gap-2 rounded-lg bg-amber-500/10 border border-amber-500/20 px-4 py-3 text-sm text-amber-400"
            role="alert"
          >
            <svg
              class="w-4 h-4 shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            {{ t('docs.article.outdated_banner') }}
          </div>

          <!-- Locale fallback notice -->
          <div
            v-if="localeFallback"
            class="mb-4 flex items-start gap-2 rounded-lg bg-slate-700/30 border border-slate-600 px-4 py-3 text-sm text-slate-400"
            role="status"
          >
            <svg
              class="w-4 h-4 shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {{ t('docs.article.locale_fallback') }}
          </div>

          <!-- Mobile "On this page" accordion -->
          <details
            v-if="article.headings.length > 0"
            class="lg:hidden mb-6 rounded-lg bg-slate-800/50 border border-slate-700"
          >
            <summary class="px-4 py-3 text-sm font-medium text-slate-300 cursor-pointer list-none flex items-center justify-between">
              {{ t('docs.article.on_this_page') }}
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </summary>
            <div class="px-4 pb-3">
              <DocsScrollspy
                :headings="article.headings"
                :active-id="activeId"
              />
            </div>
          </details>

          <!-- Title + meta -->
          <h1 class="text-2xl font-bold text-white mb-2">
            {{ article.title }}
          </h1>
          <p
            v-if="article.last_reviewed_at"
            class="text-xs text-slate-500 mb-6"
          >
            {{ t('docs.article.last_reviewed', { date: formatDate(article.last_reviewed_at) }) }}
          </p>

          <!-- Rendered Markdown body -->
          <div
            class="prose prose-invert prose-sm max-w-none
              prose-headings:text-white
              prose-p:text-slate-300
              prose-a:text-emerald-400 prose-a:no-underline hover:prose-a:underline
              prose-code:text-emerald-300 prose-code:bg-slate-800 prose-code:px-1 prose-code:rounded
              prose-pre:bg-slate-800 prose-pre:border prose-pre:border-slate-700
              prose-blockquote:border-l-emerald-500 prose-blockquote:text-slate-400
              prose-strong:text-white
              prose-li:text-slate-300
              prose-hr:border-slate-700"
            v-html="renderedHtml"
          />

          <!-- Feedback widget -->
          <DocsArticleFeedback
            :category="categoryParam"
            :slug="slugParam"
            :locale="article.locale"
          />

          <!-- Cross-link to support -->
          <div class="mt-8 pt-6 border-t border-slate-700/50">
            <p class="text-sm text-slate-500">
              {{ t('docs.article.need_help') }}
              <RouterLink
                :to="isPublic ? '/organizer/login' : '/organizer/support'"
                class="text-emerald-400 hover:underline ml-1"
              >
                {{ t('docs.article.contact_support') }}
              </RouterLink>
            </p>
          </div>
        </article>
      </main>

      <!-- Right pane: scrollspy — hidden on mobile -->
      <aside
        v-if="article && article.headings.length > 0"
        class="hidden lg:block w-56 shrink-0 border-l border-slate-800 px-3 sticky top-16 self-start max-h-[calc(100vh-4rem)] overflow-y-auto"
      >
        <DocsScrollspy
          :headings="article.headings"
          :active-id="activeId"
        />
      </aside>
    </div>

    <!-- Search palette -->
    <DocsSearchPalette
      :open="paletteOpen"
      :base="base"
      @close="paletteOpen = false"
    />
  </component>
</template>
